<template>
  <div>
    <b-alert show variant="info">{{
      $t("label.certificateSavedPreviewHint")
    }}</b-alert>
    <div v-if="loading" class="text-center p-4">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="loadError" show variant="danger"
      >{{ $t("label.certificateLoadError") }}
      <b-button size="sm" variant="outline-danger" @click="loadTemplate">{{
        $t("label.retryPermissionDefinitions")
      }}</b-button></b-alert
    >
    <form v-else @submit.prevent="generate">
      <fieldset :disabled="busy">
        <b-form-group
          v-for="field in previewFields"
          :key="field.key"
          :label="$t(field.label)"
        >
          <b-form-input
            v-if="!field.generated"
            v-model.trim="recipient[field.key]"
            required
          />
          <p v-else class="text-muted mb-0">
            {{
              $t(
                field.key === "issued_at"
                  ? "label.certificatePreviewGeneratedDate"
                  : "label.certificatePreviewGeneratedNumber",
              )
            }}
          </p>
        </b-form-group>
        <b-button type="submit" variant="info"
          ><b-spinner v-if="busy" small class="mr-1" />{{
            $t("label.certificatePdfPreview")
          }}</b-button
        >
      </fieldset>
    </form>
    <b-alert v-if="error" show variant="danger" class="mt-3">{{
      error
    }}</b-alert>
    <div class="text-right mt-3">
      <b-button variant="outline-secondary" @click="$emit('done')">{{
        $t("label.close")
      }}</b-button>
    </div>
  </div>
</template>
<script>
import { certificateService } from "@/services/api/certificate.service";
import {
  certificateModel,
  certificateRecipientPayload,
  certificatePreviewFields,
} from "@/models/api/certificate.model";
import { sharedHelper, sessionHelper } from "@/utils";
export default {
  name: "CertificatePdfPreview",
  props: { certificateUUID: { type: String, required: true } },
  data() {
    return {
      layout: null,
      recipient: { recipient_name: "Alex Tan", recipient_ic: "900101-00-1234" },
      loading: true,
      loadError: false,
      busy: false,
      error: "",
      pdfURL: "",
      requestId: 0,
    };
  },
  computed: {
    previewFields() {
      return certificatePreviewFields(this.layout);
    },
  },
  created() {
    this.loadTemplate();
  },
  beforeDestroy() {
    this.requestId++;
    this.closePendingTab();
    this.clearPdf();
  },
  methods: {
    closePendingTab() {
      if (this.pendingTab && !this.pendingTab.closed) this.pendingTab.close();
      this.pendingTab = null;
    },
    clearPdf() {
      if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
      this.pdfURL = "";
    },
    async loadTemplate() {
      const request = ++this.requestId;
      this.loading = true;
      this.loadError = false;
      this.error = "";
      this.closePendingTab();
      this.clearPdf();
      try {
        const result = await certificateService.show(this.certificateUUID);
        if (!result?.success || result.data?.uuid !== this.certificateUUID)
          throw new Error("Invalid template.");
        if (request !== this.requestId) return;
        this.layout = certificateModel(result.data).layoutConfig;
      } catch (error) {
        if (request === this.requestId) this.loadError = true;
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
    async generate() {
      if (sessionHelper.getPermissionLevel("cert_admin") < 1) return;
      if (this.busy || !this.layout || this.loading || this.loadError) return;
      let payload;
      this.error = "";
      try {
        payload = certificateRecipientPayload(this.layout, this.recipient);
      } catch (error) {
        this.error = this.$t("label.certificateRecipientRequired");
        return;
      }
      // Open synchronously from the submit gesture so browsers allow the new tab.
      const tab = window.open("about:blank", "_blank");
      if (!tab) {
        this.error = this.$t("label.certificatePopupBlocked");
        return;
      }
      tab.opener = null;
      tab.document.title = this.$t("label.certificatePdfPreview");
      tab.document.body.textContent = this.$t("label.certificatePdfLoading");
      this.pendingTab = tab;
      const request = ++this.requestId;
      this.busy = true;
      this.clearPdf();
      try {
        const blob = await certificateService.preview(
          this.certificateUUID,
          payload,
        );
        if (request !== this.requestId) return;
        if (tab.closed) return;
        this.pdfURL = URL.createObjectURL(blob);
        tab.location.replace(this.pdfURL);
        this.pendingTab = null;
      } catch (error) {
        if (request === this.requestId) {
          this.closePendingTab();
          this.clearPdf();
          this.error =
            sharedHelper.getErrorMessage(error) ||
            error?.message ||
            this.$t("label.certificatePdfError");
        }
      } finally {
        if (request === this.requestId) {
          this.closePendingTab();
          this.busy = false;
        }
      }
    },
  },
};
</script>
